<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="interactions-graph">
    <div
      v-for="(interaction, index) in interactions"
      :key="interaction.id"
      class="interaction-container"
      :class="{
        extended: consultOrConferencedWithList[index]
      }"
    >
      <div class="horizontal-graph-line" />
      <div v-if="consultOrConferencedWithList[index]" class="horizontal-graph-line-2nd-agent" />

      <!-- Originator -->
      <EcsInteractionParticipant
        :participant="getOriginator(interaction)"
        :media-type-icon="interaction.mediaTypeIcon"
        :demands="getPartyDemandsOrRole(interaction)"
        :entry-time="interaction.timing.entryTime"
        :time-prefix-label="interaction.isOffered ? $t('graph.timing.created') : ''"
        :time-suffix-label="interaction.isOffered ? $t('graph.timing.ago') : ''"
      />

      <!-- Being consulted by -->
      <EcsInteractionParticipant
        v-if="agentId !== interaction.connectedParties.handledBy.id && !interaction.isInternal"
        :participant="interaction.connectedParties.handledBy"
        :media-type-icon="interaction.mediaTypeIcon"
        :demands="interaction.isConference || interaction.isConferenced ? ['Conference with'] : ['Being consulted by']"
        :time-prefix-label="interaction.isOffered ? $t('graph.timing.created') : ''"
        :time-suffix-label="interaction.isOffered ? $t('graph.timing.ago') : ''"
      />

      <!-- Consult/Conference with -->
      <EcsInteractionParticipant
        v-else-if="consultOrConferencedWithList[index]"
        :participant="consultOrConferencedWithList[index]"
        :media-type-icon="interaction.mediaTypeIcon"
        :demands="interaction.isConference ? ['Conference with'] : ['Consulting with']"
        :time-prefix-label="interaction.isOffered ? $t('graph.timing.created') : ''"
        :time-suffix-label="interaction.isOffered ? $t('graph.timing.ago') : ''"
      />
    </div>
  </div>
</template>

<script>
import EcsInteractionParticipant from '@/components/ecs-agent-info/agent-stats-summary-card-singleton/EcsInteractionParticipant';

export default {
  name: 'InteractionsGraph',

  components: { EcsInteractionParticipant },

  props: {
    agentId: {
      type: String,
      default: ''
    },
    interactions: {
      type: Array,
      default() {
        return [];
      }
    }
  },

  computed: {
    extended() {
      return this.interaction
        ? this.interaction.connectedParties.consultingWith || this.interaction.connectedParties.conferenceWith
        : null;
    },

    consultOrConferencedWithList() {
      return this.interactions.map(
        interaction => interaction.connectedParties.consultingWith || interaction.connectedParties.conferenceWith
      );
    }
  },

  methods: {
    getOriginator(interaction) {
      if (!interaction || !interaction.connectedParties) {
        return {};
      } else if (interaction.isInternal && this.agentId !== interaction.connectedParties.handledBy.id) {
        return (interaction || {}).connectedParties.handledBy;
      } else {
        return (interaction || {}).connectedParties.originatedBy;
      }
    },
    getPartyDemandsOrRole(interaction) {
      if (interaction.isOffered) {
        return [this.$t('graph.interaction.offered')];
      } else if (interaction.routing.demands.length) {
        return interaction.routing.demands;
      } else if (interaction.isInternal) {
        return [this.$t('graph.interaction.internal')];
      } else if (interaction.isOutgoingEmail) {
        return [interaction.routing.businessProcessName, this.$t('graph.interaction.outgoingEmail')];
      } else {
        return [''];
      }
    }
  }
};
</script>

<style scoped lang="scss">
.interactions-graph {
  --thread-lines-color: var(--v-surface-darken1);

  border-radius: 8px;
  padding: 0 16px;
  position: relative;

  .horizontal-graph-line,
  .horizontal-graph-line-2nd-agent {
    width: 28px;
    height: 2px;
    position: absolute;
    top: 36px;
    left: 0;
    background-color: var(--thread-lines-color);
  }

  .horizontal-graph-line-2nd-agent {
    width: 14px;
    left: 14px;
    top: 106px;

    &:before {
      content: '';
      display: block;
      position: absolute;
      top: -68px;
      left: 0;
      height: 68px;
      width: 2px;
      background-color: var(--thread-lines-color);
    }
  }

  .interaction-container {
    margin-left: 22px;
    position: relative;
    padding: 16px 24px;

    &.extended:before {
      top: -48px;
      height: calc(100% - 60px);
    }

    &.extended + .interaction-container:before {
      height: 144px !important;
      top: -108px !important;
    }

    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 2px;
      background-color: var(--thread-lines-color);
    }

    &:first-child:before {
      height: 54px;
      top: -16px;
    }

    &:last-child:not(:first-child):before {
      height: 84px;
      top: -48px;
    }
  }
}
</style>
